import { clsx } from "clsx";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import { T } from "@repo/transifex";
import {
  Button,
  DialogError,
  DialogLoading,
  Footer,
  UnattendedLogoutButton
} from "~/components";
import { GridLayout } from "~/components/GridLayout";
import { ShopLayout } from "~/components/ShopLayout";
import { employeeActions } from "~/providers/store/employee";
import { ItemSkeleton } from "~/components/ItemSkeleton";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentSchool } from "~/hooks/queries/schools";

import { SelectorItem } from "./SelectorItem";
import { BasketWelcome } from "./basket-welcome";

export const SelectorsPage = () => {
  const navigate = useNavigate();

  const { data: school, status } = useCurrentSchool();
  const { data: till, status: tillStatus } = useCurrentTill();

  // get all the options from the school
  const allOptions = school?.groupNames ?? [];
  // filter out the designated guest selector from the options to display
  const options =
    tillStatus === "success" && till.type === "self-service"
      ? allOptions.filter((option) => option !== till.config.guestSelector)
      : [];

  // If there are 0 or 1 options skip the selector choice
  useEffect(() => {
    if (
      status === "success" &&
      tillStatus === "success" &&
      allOptions.length <= 1
    ) {
      employeeActions.setAuthEmployee({
        groupName: allOptions[0] ?? undefined
      });
      navigate("/pos");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on status
  }, [status, tillStatus]);

  if (tillStatus === "pending") return <DialogLoading />;
  if (tillStatus === "error" || till.type !== "self-service")
    return (
      <DialogError childrenPosition="top" size="md">
        <T _str="Company selection not available" />
        <Link to="/pos">
          <Button variant="danger">
            <T _str="Back" />
          </Button>
        </Link>
      </DialogError>
    );

  if (status === "pending") {
    return (
      <ShopLayout footer={<SelectorsFooter />} panel={<BasketWelcome />}>
        <GridLayout>
          {Array.from({ length: 16 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key -- fixed-length skeleton list
            <ItemSkeleton key={index} />
          ))}
        </GridLayout>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout footer={<SelectorsFooter />} panel={<BasketWelcome />}>
      <GridLayout
        className={clsx({
          "pt-40": options.length <= 12,
          "pt-60": options.length <= 8,
          "pt-80": options.length <= 4
        })}
      >
        {options.map((name) => (
          <SelectorItem groupName={name} key={name} />
        ))}
      </GridLayout>

      {till.config.guestSelector ? (
        <div
          className={clsx("mt-16 flex w-full items-center justify-center", {
            "mb-16": options.length >= 20
          })}
        >
          <Button
            onClick={() => {
              employeeActions.setAuthEmployee({
                groupName: till.config.guestSelector
              });
              navigate("/pos");
            }}
            variant="info"
          >
            <T _str="Continue as guest" />
          </Button>
        </div>
      ) : null}
    </ShopLayout>
  );
};

const SelectorsFooter = () => {
  return (
    <Footer>
      <UnattendedLogoutButton />
    </Footer>
  );
};
